import React, { useEffect, useCallback, useSyncExternalStore } from 'react';
import { useDispatch } from 'react-redux';
import notification from 'antd/es/notification';
import { useTranslation } from 'react-i18next';

import { getOpParams } from 'utils/getOpParams';
import { useGetActiveScopes } from 'utils/customHooks/useGetActiveScopes';
import { verifyScope } from 'utils/redirects';
import alarmEditRouteConfig from 'routes/AlarmDashboardPage/AlarmEditPage/route';
import { useOpNotificationOptional } from 'new-components/OpAppScaffold/components/OpNotificationProvider';

import { notificationService } from './notificationService';
import { buildNotificationActions } from './buildNotificationActions';
import { useIntercomNotificationOptional } from './IntercomNotificationProvider';
import { ClearAllAlertsButton } from './components/ClearAllAlertsButton';

import './AlertWrapper.scss';

export const AlertWrapper = React.memo(() => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const apiFromContext = useOpNotificationOptional();
  const [ownApi, contextHolder] = notification.useNotification();
  const intercomApiFromContext = useIntercomNotificationOptional();

  const api = apiFromContext ?? ownApi;

  const { orgId } = getOpParams();
  const { activeScopes } = useGetActiveScopes(orgId);
  const canEditAlarms = verifyScope(
    activeScopes,
    alarmEditRouteConfig.scope,
    orgId,
  );

  const generalOpenCount = useSyncExternalStore(
    notificationService.subscribe,
    notificationService.getGeneralOpenCount,
  );

  const intercomOpenCount = useSyncExternalStore(
    notificationService.subscribe,
    notificationService.getIntercomOpenCount,
  );

  const intercomStackHeight = useSyncExternalStore(
    notificationService.subscribe,
    notificationService.getIntercomStackHeight,
  );

  // register the intercom api before the general api so intercom alerts never
  // open on the general stack during normal mounting
  useEffect(() => {
    if (intercomApiFromContext) {
      notificationService.setIntercomApi(intercomApiFromContext);
    }
    return () => {
      notificationService.setIntercomApi(null);
    };
  }, [intercomApiFromContext]);

  useEffect(() => {
    notificationService.setActionRenderer((alert, close) =>
      buildNotificationActions(alert, close, {
        dispatch,
        t,
        canEditAlarms,
      }),
    );
    notificationService.setApi(api);
    return () => {
      notificationService.setApi(null);
      notificationService.setActionRenderer(null);
    };
  }, [api, dispatch, t, canEditAlarms]);

  const handleCloseAllGeneral = useCallback(() => {
    notificationService.clearAll('general');
  }, []);

  const handleCloseAllIntercom = useCallback(() => {
    notificationService.clearAll('intercom');
  }, []);

  return (
    <>
      {!apiFromContext && contextHolder}
      {generalOpenCount > 3 && (
        <ClearAllAlertsButton
          onClick={handleCloseAllGeneral}
          bottomOffset={
            intercomOpenCount > 0
              ? Math.max(140, intercomStackHeight + 32)
              : undefined
          }
          ariaLabel={t('Clear all other notifications')}
        />
      )}
      {intercomOpenCount > 3 && (
        <ClearAllAlertsButton
          onClick={handleCloseAllIntercom}
          ariaLabel={t('Clear all intercom notifications')}
        />
      )}
    </>
  );
});
